/**
 * Lines: the pop-out's SIP sessions and their local media. It holds no call logic (the
 * orchestrator owns that through the relay); it answers, rejects, mutes and reports.
 *
 * - `invitation.progress()` is sent synchronously inside onInvite (well within 50 ms)
 * - a third INVITE while two lines are up gets 486 and is never shown
 * - auto-answer headers (Call-Info answer-after, Alert-Info auto-answer) are never read;
 *   accepting needs a click, Enter or a HID hook event. The one exception is the browser leg
 *   of a call the agent dialled a moment ago (relay `dial`): the Dial click was the consent
 * - hold is local mute + relay `hold`; there is never a hold re-INVITE and never a REFER
 * - DTMF goes through the sender's RTCDTMFSender (RFC 4733), never SIP INFO, never logged
 */
import { SessionState, type SessionDelegate } from 'sip.js';
import { LIMITS, type CallSummary } from '@ccphone/contracts';
import type { Clock } from '../app/clock.js';
import type { Logger } from '../log.js';
import { type TimerHost, realTimers } from '../keepalive/timers.js';
import type { CommandInput } from '../relay/client.js';
import { collectStats, type StatsCounters } from '../media/stats.js';
import { eModelMos } from '../media/mos.js';
import { DTMF_OPTIONS } from './user-agent.js';

export interface SdhLike {
  readonly peerConnection?: RTCPeerConnection | undefined;
  readonly remoteMediaStream?: MediaStream;
  readonly localMediaStream?: MediaStream;
  sendDtmf(tones: string, options?: { duration: number; interToneGap: number }): boolean;
  enableSenderTracks?(enable: boolean): void;
}

export interface StateEmitterLike {
  addListener(listener: (s: SessionState) => void): void;
  removeListener(listener: (s: SessionState) => void): void;
}

export interface SessionLike {
  readonly state: SessionState;
  readonly stateChange: StateEmitterLike;
  readonly sessionDescriptionHandler: SdhLike | undefined;
  delegate: SessionDelegate | undefined;
  readonly remoteIdentity: { displayName: string; uri: { user: string | undefined } };
  bye(): Promise<unknown>;
}

export interface InvitationLike extends SessionLike {
  readonly request: { getHeader(name: string): string | undefined };
  progress(): Promise<void>;
  accept(options?: { sessionDescriptionHandlerOptions?: { constraints?: MediaStreamConstraints } }): Promise<void>;
  reject(options?: { statusCode?: number; reasonPhrase?: string }): Promise<void>;
}

export interface ProgressResponseLike {
  message: { statusCode: number | undefined; body: string };
}

export interface InviterLike extends SessionLike {
  invite(options?: { requestDelegate?: { onProgress?(r: ProgressResponseLike): void; onAccept?(r: unknown): void; onReject?(r: unknown): void } }): Promise<unknown>;
  cancel(): Promise<void>;
}

export type LineState = 'ringing' | 'dialing' | 'early' | 'up' | 'ended';
export type LostReason = 'media_lost' | 'ice_failed';

export interface LineStatsView {
  jitterMs: number;
  lossPct: number;
  rttMs: number;
  codec: string;
  relayed: boolean;
  mos: number;
  at: number;
}

export interface LineView {
  id: string;
  callId: string | null;
  direction: 'inbound' | 'outbound';
  state: LineState;
  remoteName: string;
  remoteNumber: string;
  muted: boolean;
  held: boolean;
  lost: LostReason | null;
  endedReason: string | null;
  viaTurn: boolean;
  /** clock ms */
  startedAt: number;
  answeredAt: number | null;
  heldAt: number | null;
  ringDeadline: number | null;
  pcState: RTCPeerConnectionState | 'none';
  declining: boolean;
  stats: LineStatsView | null;
  /** another line was up when this INVITE arrived */
  callWaiting: boolean;
}

interface Line extends LineView {
  session: SessionLike;
  invitation: InvitationLike | null;
  inviter: InviterLike | null;
  pc: RTCPeerConnection | null;
  disconnectTimer: unknown;
  declineTimer: unknown;
  statsPrev: StatsCounters | undefined;
  onStateChange: ((s: SessionState) => void) | null;
}

export interface LineManagerDeps {
  maxLines?: number;
  clock: Clock;
  timers?: TimerHost;
  log: Logger;
  relay: { send(cmd: CommandInput): Promise<unknown> };
  audio: {
    ringing(on: boolean): void;
    callWaitingBeep(): void;
    ringback: { start(): void; stop(): void };
    attachRemote(lineId: string, stream: MediaStream): void;
    detachRemote(lineId: string): void;
    setRemoteMuted(lineId: string, muted: boolean): void;
  };
  mediaConstraints(): MediaStreamConstraints;
  /** relay call the INVITE belongs to: by X-CC-Call-ID, else the one call awaiting its browser leg */
  matchCall(callIdHeader: string | null, attached: ReadonlySet<string>): CallSummary | undefined;
  onChange(lines: LineView[]): void;
  /** a `dial` the agent issued less than `ownDialWindowMs` ago and not yet attached */
  ownDialPending(): boolean;
  ownDialWindowMs?: number;
  inviterFactory?: (target: string, constraints: MediaStreamConstraints) => InviterLike;
  statsIntervalMs?: number;
  disconnectGraceMs?: number;
  declineSafetyMs?: number;
  endedLingerMs?: number;
  defaultRingSec?: number;
}

export const CALL_ID_HEADER = 'X-CC-Call-ID';
export const DISCONNECT_GRACE_MS = 10_000;
export const STATS_INTERVAL_MS = 10_000;
export const DECLINE_SAFETY_MS = 10_000;
export const ENDED_LINGER_MS = 6000;
export const OWN_DIAL_WINDOW_MS = 30_000;
const DTMF_RE = /^[0-9A-Da-d*#]+$/;

export class LineManager {
  private readonly lines = new Map<string, Line>();
  private seq = 0;
  private readonly timers: TimerHost;
  private readonly maxLines: number;
  private statsHandle: unknown = null;
  private stopped = false;

  constructor(private readonly d: LineManagerDeps) {
    this.timers = d.timers ?? realTimers;
    this.maxLines = d.maxLines ?? LIMITS.maxLines;
    this.statsHandle = this.timers.setInterval(() => void this.statsTick(), d.statsIntervalMs ?? STATS_INTERVAL_MS);
  }

  stop(): void {
    this.stopped = true;
    if (this.statsHandle !== null) this.timers.clearInterval(this.statsHandle);
    this.statsHandle = null;
    for (const l of this.lines.values()) this.teardown(l, 'stopped');
    this.lines.clear();
    this.d.audio.ringing(false);
    this.d.audio.ringback.stop();
  }

  // ---- views ---------------------------------------------------------------

  list(): LineView[] {
    return [...this.lines.values()].map((l) => this.view(l));
  }

  get(id: string): LineView | undefined {
    const l = this.lines.get(id);
    return l ? this.view(l) : undefined;
  }

  /** lines that are up (established or early) */
  linesUp(): number {
    let n = 0;
    for (const l of this.lines.values()) if (l.state === 'up' || l.state === 'early') n += 1;
    return n;
  }

  /** every line that is not ended (what counts towards the cap) */
  activeCount(): number {
    let n = 0;
    for (const l of this.lines.values()) if (l.state !== 'ended') n += 1;
    return n;
  }

  attachedCallIds(): Set<string> {
    const s = new Set<string>();
    for (const l of this.lines.values()) if (l.callId && l.state !== 'ended') s.add(l.callId);
    return s;
  }

  private view(l: Line): LineView {
    const { session: _s, invitation: _i, inviter: _v, pc: _p, disconnectTimer: _d, declineTimer: _t, statsPrev: _sp, onStateChange: _o, ...rest } = l;
    return { ...rest };
  }

  private emit(): void {
    this.d.onChange(this.list());
  }

  // ---- inbound -------------------------------------------------------------

  /** UserAgent delegate onInvite */
  onInvite(invitation: InvitationLike): 'ringing' | 'rejected_486' | 'auto_accepted' {
    if (this.activeCount() >= this.maxLines) {
      this.d.log.warn({ lines: this.activeCount() }, 'third INVITE rejected 486');
      invitation.reject({ statusCode: 486, reasonPhrase: 'Busy Here' }).catch(() => undefined);
      return 'rejected_486';
    }
    // 180 within 50 ms: sent synchronously, before any media work
    invitation.progress().catch((e: Error) => this.d.log.warn({ err: e.message }, 'progress failed'));

    const header = invitation.request.getHeader(CALL_ID_HEADER) ?? null;
    const call = this.d.matchCall(header, this.attachedCallIds());
    const ownDial = call?.direction === 'outbound' || (!call && this.d.ownDialPending());
    const anotherUp = this.linesUp() > 0;
    const id = `l${++this.seq}`;
    const now = this.d.clock.now();
    const line: Line = {
      id,
      callId: call?.call_id ?? header,
      direction: ownDial ? 'outbound' : 'inbound',
      state: ownDial ? 'dialing' : 'ringing',
      remoteName: call?.remote_name ?? invitation.remoteIdentity.displayName ?? '',
      remoteNumber: call?.remote_number ?? invitation.remoteIdentity.uri.user ?? '',
      muted: false,
      held: false,
      lost: null,
      endedReason: null,
      viaTurn: false,
      startedAt: now,
      answeredAt: null,
      heldAt: null,
      ringDeadline: call?.ring_deadline ? Date.parse(call.ring_deadline) : now + (this.d.defaultRingSec ?? 25) * 1000,
      pcState: 'none',
      declining: false,
      stats: null,
      callWaiting: anotherUp && !ownDial,
      session: invitation,
      invitation,
      inviter: null,
      pc: null,
      disconnectTimer: null,
      declineTimer: null,
      statsPrev: undefined,
      onStateChange: null,
    };
    this.lines.set(id, line);
    this.wireSession(line);
    if (ownDial) {
      this.d.log.info({ cc_call_id: line.callId, line: id }, 'own dial: accepting browser leg');
      this.accept(line);
      this.emit();
      return 'auto_accepted';
    }
    if (anotherUp) this.d.audio.callWaitingBeep();
    else this.d.audio.ringing(true);
    this.d.log.info({ cc_call_id: line.callId, line: id, call_waiting: anotherUp }, 'incoming call');
    this.emit();
    return 'ringing';
  }

  /** attach relay call summaries to lines that arrived before (or without) their event */
  rematch(): void {
    let changed = false;
    for (const l of this.lines.values()) {
      if (l.state === 'ended') continue;
      const header = l.invitation?.request.getHeader(CALL_ID_HEADER) ?? l.callId;
      if (l.callId && header === l.callId && l.remoteName) continue;
      const call = this.d.matchCall(header, this.attachedCallIds());
      if (!call) continue;
      if (l.callId !== call.call_id || l.remoteName !== (call.remote_name ?? l.remoteName)) {
        l.callId = call.call_id;
        if (call.remote_name) l.remoteName = call.remote_name;
        if (call.remote_number) l.remoteNumber = call.remote_number;
        if (call.ring_deadline) l.ringDeadline = Date.parse(call.ring_deadline);
        changed = true;
      }
    }
    if (changed) this.emit();
  }

  async answer(id: string): Promise<void> {
    const l = this.lines.get(id);
    if (!l || !l.invitation || l.state !== 'ringing') return;
    // call waiting: a single hold path, relay hold for the other line first
    for (const other of this.lines.values()) {
      if (other.id !== id && other.state === 'up' && !other.held) await this.hold(other.id, true);
    }
    this.accept(l);
    this.emit();
    if (l.callId) {
      // informational: the SIP accept is authoritative; lets the relay stop the ring timer early
      this.d.relay.send({ t: 'answer', call_id: l.callId }).catch(() => undefined);
    }
  }

  private accept(l: Line): void {
    if (!l.invitation) return;
    this.d.audio.ringing(false);
    l.invitation
      .accept({ sessionDescriptionHandlerOptions: { constraints: this.d.mediaConstraints() } })
      .catch((e: Error) => {
        this.d.log.warn({ err: e.message, line: l.id }, 'accept failed');
        this.end(l, 'accept_failed');
      });
  }

  /** Decline and Send to voicemail both go to the relay; the orchestrator answers 480 upstream */
  async decline(id: string, toVoicemail = false): Promise<void> {
    const l = this.lines.get(id);
    if (!l || l.state !== 'ringing') return;
    l.declining = true;
    this.d.audio.ringing(false);
    this.emit();
    if (l.callId) {
      try {
        await this.d.relay.send({ t: 'decline', call_id: l.callId, to_voicemail: toVoicemail });
      } catch (e) {
        this.d.log.warn({ err: (e as Error).message, cc_call_id: l.callId }, 'relay decline failed; rejecting locally');
        this.rejectLocally(l, 603);
        return;
      }
      // the orchestrator cancels the browser leg; if that never comes, end it locally
      l.declineTimer = this.timers.setTimeout(() => {
        if (l.state === 'ringing') this.rejectLocally(l, 603);
      }, this.d.declineSafetyMs ?? DECLINE_SAFETY_MS);
    } else {
      this.rejectLocally(l, 603);
    }
  }

  private rejectLocally(l: Line, statusCode: number): void {
    l.invitation?.reject({ statusCode }).catch(() => undefined);
    this.end(l, 'declined');
  }

  // ---- outbound (direct Inviter path; the default path is relay `dial`) -------

  invite(target: string): string | null {
    if (!this.d.inviterFactory) return null;
    if (this.activeCount() >= this.maxLines) return null;
    const inviter = this.d.inviterFactory(target, this.d.mediaConstraints());
    const id = `l${++this.seq}`;
    const now = this.d.clock.now();
    const line: Line = {
      id,
      callId: null,
      direction: 'outbound',
      state: 'dialing',
      remoteName: '',
      remoteNumber: target,
      muted: false,
      held: false,
      lost: null,
      endedReason: null,
      viaTurn: false,
      startedAt: now,
      answeredAt: null,
      heldAt: null,
      ringDeadline: null,
      pcState: 'none',
      declining: false,
      stats: null,
      callWaiting: false,
      session: inviter,
      invitation: null,
      inviter,
      pc: null,
      disconnectTimer: null,
      declineTimer: null,
      statsPrev: undefined,
      onStateChange: null,
    };
    this.lines.set(id, line);
    this.wireSession(line);
    inviter
      .invite({
        requestDelegate: {
          onProgress: (r) => {
            const code = r.message.statusCode ?? 0;
            if (code === 180 && !r.message.body) {
              this.d.audio.ringback.start();
            } else {
              // 183 or 180 with SDP: early media from the far end
              this.d.audio.ringback.stop();
              if (code === 183 || r.message.body) line.state = 'early';
            }
            this.emit();
          },
          onAccept: () => this.d.audio.ringback.stop(),
          onReject: () => this.d.audio.ringback.stop(),
        },
      })
      .catch((e: Error) => {
        this.d.log.warn({ err: e.message, line: id }, 'invite failed');
        this.d.audio.ringback.stop();
        this.end(line, 'invite_failed');
      });
    this.emit();
    return id;
  }

  // ---- in-call controls -----------------------------------------------------

  /**
   * Hang up. A relay call is ended by the orchestrator (relay `hangup`), which hangs up every leg, the browser leg
   * included: one authoritative hangup. Sending a BYE as well raced the command: the gateway saw the browser leg
   * vanish first, took it for a lost pop-out and parked the customer for Rejoin (MOH, `call.rejoinable`) until the
   * command arrived. The local microphone is cut at once; the BYE is only the fallback when the relay cannot deliver
   * the command (nack, timeout, relay down). Calls without a relay call id (direct Inviter) end with BYE / CANCEL.
   */
  async hangup(id: string): Promise<void> {
    const l = this.lines.get(id);
    if (!l) return;
    if (l.state === 'ringing' && l.invitation) {
      await this.decline(id, false);
      return;
    }
    const session = l.session;
    const live = (): boolean => session.state === SessionState.Established || session.state === SessionState.Establishing;
    if (l.inviter && (l.state === 'dialing' || l.state === 'early')) {
      l.inviter.cancel().catch(() => undefined);
      if (l.callId) this.d.relay.send({ t: 'hangup', call_id: l.callId }).catch(() => undefined);
    } else if (l.callId) {
      this.muteSender(l);
      this.d.relay.send({ t: 'hangup', call_id: l.callId }).catch((e: Error) => {
        this.d.log.warn({ err: e.message, cc_call_id: l.callId }, 'relay hangup failed; ending the SIP session locally');
        if (live()) session.bye().catch(() => undefined);
      });
    } else if (live()) {
      session.bye().catch(() => undefined);
    }
    this.end(l, 'hangup');
  }

  /** stop sending audio now (hang up in progress); the session itself ends when the gateway hangs the leg up */
  private muteSender(l: Line): void {
    const sdh = l.session.sessionDescriptionHandler;
    if (sdh?.enableSenderTracks) sdh.enableSenderTracks(false);
    else for (const t of sdh?.localMediaStream?.getAudioTracks() ?? []) t.enabled = false;
  }

  /** Hold = mute local tracks + relay `hold`; Resume = tracks back + relay `unhold` */
  async hold(id: string, on: boolean): Promise<void> {
    const l = this.lines.get(id);
    if (!l || l.state !== 'up') return;
    l.held = on;
    l.heldAt = on ? this.d.clock.now() : null;
    this.applyLocalTracks(l);
    this.d.audio.setRemoteMuted(l.id, on);
    this.emit();
    if (l.callId) {
      try {
        await this.d.relay.send(on ? { t: 'hold', call_id: l.callId } : { t: 'unhold', call_id: l.callId });
      } catch (e) {
        // revert the optimistic state; the toast is the caller's
        l.held = !on;
        l.heldAt = l.held ? this.d.clock.now() : null;
        this.applyLocalTracks(l);
        this.d.audio.setRemoteMuted(l.id, l.held);
        this.emit();
        throw e;
      }
    }
  }

  /** Mute = track.enabled=false + relay `mute` (belt and braces) */
  async mute(id: string, on: boolean): Promise<void> {
    const l = this.lines.get(id);
    if (!l || l.state === 'ended') return;
    l.muted = on;
    this.applyLocalTracks(l);
    this.emit();
    if (l.callId) await this.d.relay.send({ t: 'mute', call_id: l.callId, muted: on }).catch(() => undefined);
  }

  /** the remote side held us (call.held by:remote) or resumed */
  setRemoteHeld(callId: string, held: boolean): void {
    for (const l of this.lines.values()) {
      if (l.callId !== callId || l.state !== 'up') continue;
      if (held && !l.held) {
        l.heldAt = this.d.clock.now();
      }
      if (!held && l.heldAt !== null && !l.held) l.heldAt = null;
      this.emit();
    }
  }

  private applyLocalTracks(l: Line): void {
    const enable = !l.muted && !l.held;
    const sdh = l.session.sessionDescriptionHandler;
    if (sdh?.enableSenderTracks) {
      sdh.enableSenderTracks(enable);
      return;
    }
    for (const t of sdh?.localMediaStream?.getAudioTracks() ?? []) t.enabled = enable;
  }

  sendDtmf(id: string, tones: string): boolean {
    const l = this.lines.get(id);
    if (!l || l.state !== 'up' || !DTMF_RE.test(tones)) return false;
    const sdh = l.session.sessionDescriptionHandler;
    if (!sdh) return false;
    const ok = sdh.sendDtmf(tones, DTMF_OPTIONS);
    // digits are never logged
    this.d.log.debug({ line: id, count: tones.length, ok }, 'dtmf');
    return ok;
  }

  /** replace the sender's audio track (device change) on every live line */
  async replaceMicTrack(getTrack: () => Promise<MediaStreamTrack>): Promise<number> {
    let n = 0;
    for (const l of this.lines.values()) {
      if (l.state !== 'up' || !l.pc) continue;
      const sender = l.pc.getSenders().find((s) => s.track?.kind === 'audio');
      if (!sender) continue;
      try {
        const track = await getTrack();
        track.enabled = !l.muted && !l.held;
        await sender.replaceTrack(track);
        n += 1;
      } catch (e) {
        this.d.log.warn({ err: (e as Error).message, line: l.id }, 'replaceTrack failed');
      }
    }
    return n;
  }

  // ---- session wiring --------------------------------------------------------

  private wireSession(l: Line): void {
    const prevDelegate = l.session.delegate;
    l.session.delegate = {
      ...prevDelegate,
      onSessionDescriptionHandler: (sdh, provisional) => {
        prevDelegate?.onSessionDescriptionHandler?.(sdh, provisional);
        this.onSdh(l, sdh as unknown as SdhLike);
      },
      onBye: (bye) => {
        prevDelegate?.onBye?.(bye);
        this.d.log.info({ line: l.id, cc_call_id: l.callId }, 'remote BYE');
      },
    };
    const listener = (s: SessionState): void => this.onSessionState(l, s);
    l.onStateChange = listener;
    l.session.stateChange.addListener(listener);
    if (!l.session.sessionDescriptionHandler) return;
    this.onSdh(l, l.session.sessionDescriptionHandler);
  }

  private onSdh(l: Line, sdh: SdhLike): void {
    const pc = sdh.peerConnection;
    if (!pc || l.pc === pc) return;
    l.pc = pc;
    l.pcState = pc.connectionState ?? 'new';
    pc.addEventListener('connectionstatechange', () => this.onPcState(l, pc));
    if (sdh.remoteMediaStream) this.d.audio.attachRemote(l.id, sdh.remoteMediaStream);
    this.applyLocalTracks(l);
  }

  private onPcState(l: Line, pc: RTCPeerConnection): void {
    l.pcState = pc.connectionState;
    if (pc.connectionState === 'connected') {
      if (l.disconnectTimer !== null) this.timers.clearTimeout(l.disconnectTimer);
      l.disconnectTimer = null;
      this.emit();
      return;
    }
    if (pc.connectionState === 'failed') {
      this.markLost(l, l.state === 'up' ? 'media_lost' : 'ice_failed');
      return;
    }
    if (pc.connectionState === 'disconnected' && l.disconnectTimer === null) {
      l.disconnectTimer = this.timers.setTimeout(() => {
        l.disconnectTimer = null;
        if (l.pc?.connectionState !== 'connected' && l.state !== 'ended') this.markLost(l, 'media_lost');
      }, this.d.disconnectGraceMs ?? DISCONNECT_GRACE_MS);
    }
    this.emit();
  }

  private markLost(l: Line, reason: LostReason): void {
    if (l.lost) return;
    l.lost = reason;
    this.d.log.warn({ line: l.id, cc_call_id: l.callId, reason }, 'call lost');
    l.session.bye().catch(() => undefined);
    if (l.callId) this.d.relay.send({ t: 'hangup', call_id: l.callId }).catch(() => undefined);
    this.end(l, reason);
  }

  private onSessionState(l: Line, s: SessionState): void {
    if (s === SessionState.Established) {
      l.state = 'up';
      l.answeredAt = l.answeredAt ?? this.d.clock.now();
      this.d.audio.ringing(false);
      this.d.audio.ringback.stop();
      const sdh = l.session.sessionDescriptionHandler;
      if (sdh) this.onSdh(l, sdh);
      this.applyLocalTracks(l);
      this.emit();
    } else if (s === SessionState.Terminated) {
      // a decline is answered by the orchestrator hanging up the browser leg (SIP CANCEL): still the agent's decline
      if (l.state !== 'ended') this.end(l, l.lost ?? (l.answeredAt ? 'remote_hangup' : l.direction === 'inbound' ? (l.declining ? 'declined' : 'cancelled') : 'failed'));
    }
  }

  /** an external reason ended the call (server restart, call.ended from the relay) */
  endByCallId(callId: string, reason: string): void {
    for (const l of this.lines.values()) if (l.callId === callId && l.state !== 'ended') this.end(l, reason);
  }

  endAll(reason: string): void {
    for (const l of this.lines.values()) if (l.state !== 'ended') this.end(l, reason);
  }

  private end(l: Line, reason: string): void {
    if (l.state === 'ended') return;
    l.state = 'ended';
    l.endedReason = reason;
    this.teardown(l, reason);
    if (![...this.lines.values()].some((x) => x.state === 'ringing' && !x.declining)) this.d.audio.ringing(false);
    this.emit();
    this.timers.setTimeout(() => {
      this.lines.delete(l.id);
      this.emit();
    }, this.d.endedLingerMs ?? ENDED_LINGER_MS);
  }

  private teardown(l: Line, reason: string): void {
    if (l.disconnectTimer !== null) this.timers.clearTimeout(l.disconnectTimer);
    if (l.declineTimer !== null) this.timers.clearTimeout(l.declineTimer);
    l.disconnectTimer = null;
    l.declineTimer = null;
    if (l.onStateChange) l.session.stateChange.removeListener(l.onStateChange);
    this.d.audio.detachRemote(l.id);
    if (reason === 'stopped' || reason === 'server_restart') l.session.bye().catch(() => undefined);
  }

  // ---- quality ----------------------------------------------------------------

  async statsTick(): Promise<void> {
    if (this.stopped) return;
    for (const l of this.lines.values()) {
      if (l.state !== 'up' || !l.pc) continue;
      try {
        const s = await collectStats(l.pc, l.statsPrev);
        l.statsPrev = s.counters;
        const mos = eModelMos({ rttMs: s.rttMs, jitterMs: s.jitterMs, lossPct: s.lossPct, codec: s.codec });
        l.stats = { jitterMs: s.jitterMs, lossPct: s.lossPct, rttMs: s.rttMs, codec: s.codec, relayed: s.relayed, mos, at: this.d.clock.now() };
        l.viaTurn = s.relayed;
        if (l.callId) {
          this.d.relay
            .send({ t: 'stats', call_id: l.callId, jitter_ms: s.jitterMs, loss_pct: s.lossPct, rtt_ms: s.rttMs, codec: s.codec, relayed: s.relayed, mos })
            .catch(() => undefined);
        }
      } catch (e) {
        this.d.log.debug({ err: (e as Error).message, line: l.id }, 'getStats failed');
      }
    }
    this.emit();
  }
}

export const isUp = (l: LineView): boolean => l.state === 'up' || l.state === 'early';
